import React, { useState, useEffect } from 'react';
import loadImage from '../assets/imageLoader';
import { FaCalendar, FaTransgender, FaClipboardList, FaWeight, FaArrowsAltV, FaDumbbell } from 'react-icons/fa';
import apiClient from '../api/api';

const EditWorkerProfileModal = ({ showModal, onClose, workerData, onSave }) => {
  // States for form fields
  const [name, setName] = useState('');
  const [nid_or_passport, setNid_or_passport] = useState('');
  const [date_of_birth, setDate_of_birth] = useState('');
  const [gender, setGender] = useState('');
  const [experience_year, setExperience_year] = useState('');
  const [payout_method, setPayout_method] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [strength, setStrength] = useState('');
  const [personal_cv, setPersonal_cv] = useState('');
  const [preferred_working_time, setPreferred_working_time] = useState('');
  const [expected_wage, setExpected_wage] = useState('');
  const [about_me, setAbout_me] = useState('');

  // Address states
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedUpazila, setSelectedUpazila] = useState('');
  const [granularLocation, setGranularLocation] = useState('');
  const [postCode, setPostCode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  // Category and Image states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  
  // Form validation and error states
  const [isFormValid, setIsFormValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // States for fetched data
  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [upazilas, setUpazilas] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(true);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [upazilasLoading, setUpazilasLoading] = useState(false);
  const [locationsError, setLocationsError] = useState(null);
  
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);

  // ... (useEffect for populating form)

  // Fetch initial static data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setDivisionsLoading(true);
        setCategoriesLoading(true);
        const [divResponse, catResponse] = await Promise.all([
          apiClient.get('/location-data/divisions'),
          apiClient.get('/categories'),
        ]);
        setDivisions(['', ...divResponse.data]);
        setCategories(catResponse.data); // Correctly store the array
      } catch (err) {
        setLocationsError('Failed to load initial form data.');
        setCategoriesError('Failed to load categories.');
        console.error(err);
      } finally {
        setDivisionsLoading(false);
        setCategoriesLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch districts when division changes
  useEffect(() => {
    if (!selectedDivision) {
      setDistricts([]);
      return;
    }
    const fetchDistricts = async () => {
      setDistrictsLoading(true);
      try {
        const response = await apiClient.get(`/location-data/districts/${selectedDivision}`);
        setDistricts(['', ...response.data]);
      } catch (err) {
        setLocationsError('Failed to load districts.');
      } finally {
        setDistrictsLoading(false);
      }
    };
    fetchDistricts();
  }, [selectedDivision]);

  // Fetch upazilas when district changes
  useEffect(() => {
    if (!selectedDistrict) {
      setUpazilas([]);
      return;
    }
    const fetchUpazilas = async () => {
      setUpazilasLoading(true);
      try {
        const response = await apiClient.get(`/location-data/thanas/${selectedDistrict}`);
        setUpazilas(['', ...response.data]);
      } catch (err) {
        setLocationsError('Failed to load upazilas.');
      } finally {
        setUpazilasLoading(false);
      }
    };
    fetchUpazilas();
  }, [selectedDistrict]);

  // Form validation effect
  useEffect(() => {
    setIsFormValid(
      name && nid_or_passport && date_of_birth && gender && experience_year &&
      selectedDivision && selectedDistrict && selectedUpazila && granularLocation && postCode &&
      selectedCategories.length > 0
    );
  }, [name, nid_or_passport, date_of_birth, gender, experience_year, selectedDivision, selectedDistrict, selectedUpazila, granularLocation, postCode, selectedCategories]);

  if (!showModal) return null;

  const handleSave = () => {
    if (!isFormValid) {
      setErrorMessage('Please fill all required fields.');
      return;
    }
    const updatedProfileData = {
      name, date_of_birth, gender, nid_or_passport,
      location: { 
        division: selectedDivision, 
        district: selectedDistrict, 
        upazila: selectedUpazila, 
        granular_location: granularLocation,
        postCode,
        latitude,
        longitude
      },
      picture: uploadedImage ? uploadedImage.name : workerData.picture,
    };
    const updatedWorkerSpecificData = {
      experience_year, payout_method, height, weight, strength, personal_cv,
      preferred_working_category: selectedCategories,
    };
    onSave({ profile: updatedProfileData, worker: updatedWorkerSpecificData });
    onClose();
  };
  
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB limit
        alert("Image size should not exceed 1MB.");
        return;
      }
      setUploadedImage(file);
      setSelectedImage(URL.createObjectURL(file));
    }
  };

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setSelectedCategories(prev => checked ? [...prev, value] : prev.filter(c => c !== value));
  };
  
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full" id="my-modal">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <h3 className="text-lg leading-6 font-medium text-gray-900 text-center">Edit Personal Information</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column: Picture */}
          <div className="md:col-span-1 flex flex-col items-center">
            <img src={selectedImage} alt="Worker" className="w-32 h-32 rounded-full object-cover border-4 border-green-500" />
            <input type="file" id="profile-picture-upload" accept="image/*" onChange={handleImageChange} className="mt-4 text-sm" />
          </div>

          {/* Right Column: Form Fields */}
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Input fields for Profile model */}
            <input name="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name *" required />
            <input name="nid_or_passport" value={nid_or_passport} onChange={(e) => setNid_or_passport(e.target.value)} placeholder="NID/Passport *" required />
            <input name="date_of_birth" type="date" value={date_of_birth} onChange={(e) => setDate_of_birth(e.target.value)} required />
            <select name="gender" value={gender} onChange={(e) => setGender(e.target.value)} required>
              <option value="">Select Gender *</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            
            {/* Input fields for Worker model */}
            <input name="experience_year" type="number" value={experience_year} onChange={(e) => setExperience_year(e.target.value)} placeholder="Experience (Years) *" required />
            <input name="payout_method" value={payout_method} onChange={(e) => setPayout_method(e.target.value)} placeholder="Payout Method" />
            <input name="height" type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="Height (cm)" />
            <input name="weight" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="Weight (kg)" />
            <input name="strength" type="number" value={strength} onChange={(e) => setStrength(e.target.value)} placeholder="Strength (kg) *" required />
            <input name="personal_cv" value={personal_cv} onChange={(e) => setPersonal_cv(e.target.value)} placeholder="CV URL" />

            {/* Address fields */}
            <select value={selectedDivision} onChange={(e) => { setSelectedDivision(e.target.value); setSelectedDistrict(''); setSelectedUpazila(''); }} required disabled={divisionsLoading}>
              <option value="">{divisionsLoading ? 'Loading...' : 'Select Division *'}</option>
              {locationsError ? <option>{locationsError}</option> : divisions.map(div => <option key={div} value={div}>{div}</option>)}
            </select>
            <select value={selectedDistrict} onChange={(e) => { setSelectedDistrict(e.target.value); setSelectedUpazila(''); }} required disabled={!selectedDivision}>
              <option value="">Select District *</option>
              {districts.map(dist => <option key={dist} value={dist}>{dist}</option>)}
            </select>
            <select value={selectedUpazila} onChange={(e) => setSelectedUpazila(e.target.value)} required disabled={!selectedDistrict}>
              <option value="">Select Upazila *</option>
              {upazilas.map(upz => <option key={upz} value={upz}>{upz}</option>)}
            </select>
            <input name="granular_location" value={granularLocation} onChange={(e) => setGranularLocation(e.target.value)} placeholder="Village / Street *" required disabled={!selectedUpazila} />
            <input name="postCode" value={postCode} onChange={(e) => setPostCode(e.target.value)} placeholder="Post Code *" required className="sm:col-span-2" />
            <input name="latitude" value={latitude} onChange={(e) => setLatitude(e.target.value)} placeholder="Latitude (Optional)" />
            <input name="longitude" value={longitude} onChange={(e) => setLongitude(e.target.value)} placeholder="Longitude (Optional)" />
            
            {/* Category Checkboxes */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Categories *</label>
              {categoriesLoading ? <p>Loading categories...</p> : categoriesError ? <p className="text-red-500">{categoriesError}</p> :
                <div className="grid grid-cols-2 gap-2">
              {categories.map(cat => (
                    <div key={cat.id} className="flex items-center">
                      <input type="checkbox" id={cat.id} value={cat.Name} checked={selectedCategories.includes(cat.Name)} onChange={handleCategoryChange} />
                      <label htmlFor={cat.id} className="ml-2">{cat.Name}</label>
                    </div>
                  ))}
                </div>
              }
            </div>
          </div>
        </div>
        {errorMessage && <p className="text-red-500 text-center text-sm mt-4">{errorMessage}</p>}
        <div className="flex justify-end items-center px-4 py-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md mr-2 hover:bg-gray-400">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400" disabled={!isFormValid}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default EditWorkerProfileModal;
